import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import tripService from '../services/tripService';
import { Calendar, MapPin, Users, Clock } from 'lucide-react';

const SharedTrip = () => {
  const { shareLink } = useParams();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSharedTrip = async () => {
      try {
        const tripData = await tripService.getSharedTrip(shareLink);
        setTrip(tripData);
      } catch (error) {
        setError('Trip not found or no longer available');
      } finally {
        setLoading(false);
      }
    };

    fetchSharedTrip();
  }, [shareLink]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{error}</h2>
          <p className="text-gray-600">The trip you're looking for might have been removed or made private.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Trip Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{trip.name}</h1>
          <p className="text-gray-600 mb-4">{trip.description}</p>
          
          <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              <span>
                {format(new Date(trip.startDate), 'MMM dd')} - {format(new Date(trip.endDate), 'MMM dd, yyyy')}
              </span>
            </div>
            <div className="flex items-center">
              <MapPin className="h-4 w-4 mr-1" />
              <span>{trip.totalDays} days</span>
            </div>
            <div className="flex items-center">
              <Users className="h-4 w-4 mr-1" />
              <span>{trip.participants.length + 1} travelers</span>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Created by:</span>
            <div className="flex items-center space-x-2">
              <img
                src={`https://ui-avatars.com/api/?name=${trip.owner.name}&size=32&background=3b82f6&color=fff`}
                alt={trip.owner.name}
                className="w-8 h-8 rounded-full"
              />
              <span className="text-sm text-gray-600">{trip.owner.name}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Itinerary */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Itinerary</h2>
        
        {Array.from({ length: trip.totalDays }, (_, index) => {
          const day = index + 1;
          const dayItinerary = trip.itinerary.find(item => item.day === day);
          
          return (
            <div key={day} className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Day {day}</h3>

              {dayItinerary?.places?.length > 0 ? (
                <div className="space-y-4">
                  {dayItinerary.places.map((place, placeIndex) => (
                    <div key={placeIndex} className="border-l-4 border-primary-500 pl-4">
                      <div>
                        <h4 className="font-semibold text-gray-900">{place.name}</h4>
                        {place.address && (
                          <p className="text-sm text-gray-600 flex items-center mt-1">
                            <MapPin className="h-3 w-3 mr-1" />
                            {place.address}
                          </p>
                        )}
                        {place.timing && (
                          <p className="text-sm text-gray-600 flex items-center mt-1">
                            <Clock className="h-3 w-3 mr-1" />
                            {place.timing}
                          </p>
                        )}
                        {place.activities?.length > 0 && (
                          <div className="mt-2">
                            <span className="text-sm font-medium text-gray-700">Activities: </span>
                            <span className="text-sm text-gray-600">{place.activities.join(', ')}</span>
                          </div>
                        )}
                        {place.notes && (
                          <p className="text-sm text-gray-600 mt-2">{place.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No places planned for this day</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-12 text-center">
        <p className="text-gray-600">
          Want to create your own travel plans? 
          <a href="/register" className="text-primary-600 hover:text-primary-700 font-medium ml-1">
            Sign up for TravelPlanner
          </a>
        </p>
      </div>
    </div>
  );
};

export default SharedTrip;